/**
 * useRoleLabel — the firm's own titles for the firm-staff roles, resolved.
 *
 * 2026-08-22 (Klaprops meeting): firms want their own word for "Relationship
 * manager". The rename is COSMETIC by construction — `labelForRole` only ever
 * changes the string a human reads; every gate still reads the canonical role
 * key through `lib/authz/*`, and the four renameable keys are a closed set
 * (never `super_admin`, never the statutory titles).
 *
 * Why a hook rather than another local map: seven surfaces each carried their
 * own `ROLE_LABELS` object, three of them disagreeing on the wording
 * ("Administrator" / "Admin", "Compliance officer" / "Compliance"), and the
 * sidebar rendered the raw slug. Adding per-firm titles to seven copies would
 * have shipped seven chances to miss one, so the copies are gone and this is
 * the only path.
 *
 * Reads `GET /v1/tenants/me` — the one authed call every console surface
 * already makes, so no screen pays for an extra round-trip. Imported from the
 * `lib/api` barrel deliberately: that is the module screens already stub in
 * unit tests, so a test that mocks the API surface keeps mocking this too.
 */

import { useTenantMe } from '../api';
import {
  labelForRole,
  resolveRoleDisplay,
  type CustomRoleLike,
  type RoleLabelOverrides,
} from './index';

/** Stable empty object — a fresh `{}` per render would re-fire memo deps in
 *  any consumer that keys on the overrides. */
const NO_OVERRIDES: RoleLabelOverrides = {};
/** Stable empty list, for the same reason. */
const NO_CUSTOM_ROLES: CustomRoleLike[] = [];

/** The firm's role-title overrides, or `{}` while the tenant read is in flight
 *  (which renders the canonical Elreign names — never a blank role tag). */
export function useRoleLabelOverrides(): RoleLabelOverrides {
  const tenantQ = useTenantMe();
  return tenantQ.data?.roleLabels ?? NO_OVERRIDES;
}

/**
 * A `role → label` resolver bound to the current firm's titles.
 *
 * ```tsx
 * const roleLabel = useRoleLabel();
 * <span className="role-tag">{roleLabel(member.role)}</span>
 * ```
 */
export function useRoleLabel(): (role: string | undefined | null) => string {
  const overrides = useRoleLabelOverrides();
  return (role) => labelForRole(role, overrides);
}

/** The firm's INVENTED role names, or `[]` while the tenant read is in flight
 *  (which renders canonical names — never a blank role tag). */
export function useCustomRoles(): CustomRoleLike[] {
  const tenantQ = useTenantMe();
  return tenantQ.data?.customRoles ?? NO_CUSTOM_ROLES;
}

/**
 * The member-aware resolver: the same three-step fallback as
 * {@link resolveRoleDisplay}, bound to the current firm.
 *
 * ```tsx
 * const roleDisplay = useRoleDisplay();
 * <span className="role-tag">{roleDisplay(member.role, member.roleLabelId)}</span>
 * ```
 *
 * Use this wherever a specific PERSON's role is rendered; `useRoleLabel` above
 * remains correct where only a canonical role is in hand (a picker option, a
 * capability note) and there is no member to be specific about.
 */
export function useRoleDisplay(): (
  role: string | undefined | null,
  roleLabelId?: string | undefined,
) => string {
  const overrides = useRoleLabelOverrides();
  const customRoles = useCustomRoles();
  return (role, roleLabelId) =>
    resolveRoleDisplay(role, { roleLabelId, customRoles, overrides });
}
